'use client';

import { FC } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  IconButton,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { ProjectCollaboratorDto, RoleDto } from '@/api/ApiClient';

interface ProjectCollaboratorProps {
  collaborator: ProjectCollaboratorDto;
  isReadOnly: boolean;
  availableRoles: RoleDto[];
  rolesLoading?: boolean;
  onUpdate: (collaborator: ProjectCollaboratorDto) => void;
  onRemove: () => void;
}

const ProjectCollaborator: FC<ProjectCollaboratorProps> = ({
  collaborator,
  isReadOnly,
  availableRoles,
  rolesLoading = false,
  onUpdate,
  onRemove,
}) => {
  const handleRoleChange = (roleId: number) => {
    const selectedRole = availableRoles.find((role) => role.id === roleId);
    onUpdate({
      ...collaborator,
      roleId,
      role: selectedRole?.name || '',
    });
  };

  if (isReadOnly && !collaborator.id) {
    return null;
  }

  return (
    <TableRow>
      <TableCell>
        <TextField
          value={`${collaborator.firstName || ''} ${collaborator.lastName || ''}`.trim()}
          size="small"
          fullWidth
          InputProps={{ readOnly: true }}
          variant={isReadOnly ? 'standard' : 'outlined'}
          sx={
            isReadOnly
              ? {
                  '& .MuiInput-underline:before': { borderBottom: 'none' },
                  '& .MuiInput-underline:after': { borderBottom: 'none' },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                }
              : {}
          }
        />
      </TableCell>
      <TableCell>
        {isReadOnly ? (
          <TextField
            value={collaborator.role || ''}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            variant="standard"
            sx={{
              '& .MuiInput-underline:before': { borderBottom: 'none' },
              '& .MuiInput-underline:after': { borderBottom: 'none' },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
            }}
          />
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel>Role</InputLabel>
            <Select
              value={collaborator.roleId || ''}
              onChange={(e) => handleRoleChange(Number(e.target.value))}
              label="Role"
              disabled={rolesLoading}
            >
              {rolesLoading ? (
                <MenuItem disabled>Loading roles...</MenuItem>
              ) : availableRoles.length === 0 ? (
                <MenuItem disabled>No roles available</MenuItem>
              ) : (
                availableRoles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}
      </TableCell>
      <TableCell>
        <TextField
          value={collaborator.institution || ''}
          size="small"
          fullWidth
          InputProps={{ readOnly: true }}
          variant={isReadOnly ? 'standard' : 'outlined'}
          sx={
            isReadOnly
              ? {
                  '& .MuiInput-underline:before': { borderBottom: 'none' },
                  '& .MuiInput-underline:after': { borderBottom: 'none' },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                }
              : {}
          }
        />
      </TableCell>
      {!isReadOnly ? (
        <TableCell>
          <Tooltip title="Remove collaborator">
            <IconButton size="small" onClick={onRemove} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      ) : null}
    </TableRow>
  );
};

export default ProjectCollaborator;
